import type { ComponentProps } from 'react'
import { Item, itemDescriptionVariants, itemTitleClasses } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

/**
 * The settings rows are `@highlight/ui` Item: a flat column with a hairline between rows. Title and
 * description take Item's type but not its truncation, because settings copy is long and must read in full.
 */
function SettingsList({ className, children, ...props }: ComponentProps<typeof Item.Group>) {
  return (
    <Item.Group data-slot="list" className={className} {...props}>
      {children}
    </Item.Group>
  )
}

interface SettingsListItemProps extends ComponentProps<typeof Item> {
  /** Draws the eye to a row a deep link landed on. */
  highlight?: boolean
}

function SettingsListItem({ className, children, highlight, ...props }: SettingsListItemProps) {
  return (
    <Item
      className={twMerge(highlight && 'bg-brand-subtle -mx-3 w-auto animate-pulse rounded-xl px-3', className)}
      {...props}
    >
      {children}
    </Item>
  )
}

function SettingsListItemTitleGroup({ className, children, ...props }: ComponentProps<typeof Item.Content>) {
  return (
    <Item.Content className={twMerge('gap-1 [&_svg]:size-3', className)} {...props}>
      {children}
    </Item.Content>
  )
}

function SettingsListItemTitle({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="list-item-title"
      className={twMerge(itemTitleClasses, 'flex items-center gap-2 [&_svg]:size-3', className)}
      {...props}
    >
      {children}
    </div>
  )
}

function SettingsListItemDescription({ className, children, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="list-item-subtitle"
      className={twMerge(itemDescriptionVariants(), 'm-0 text-pretty break-words', className)}
      {...props}
    >
      {children}
    </p>
  )
}

function SettingsListItemActionsGroup({ className, children, ...props }: ComponentProps<typeof Item.Actions>) {
  return (
    <Item.Actions className={twMerge('flex-wrap justify-end', className)} {...props}>
      {children}
    </Item.Actions>
  )
}

export {
  SettingsList,
  SettingsListItem,
  SettingsListItemTitleGroup,
  SettingsListItemTitle,
  SettingsListItemDescription,
  SettingsListItemActionsGroup,
}
